import {
  closeAIFeedbackToast,
  openAIFeedbackToast,
  sendAIFeedback,
  setAIToastStep,
  setTechniqueFeedbackValue,
} from "domain/ai-search/ai-search.actions";
import {
  selectAIToastStep,
  selectIsAIFeedbackToastOpen,
} from "domain/ai-search/ai-search.selectors";
import { useState } from "react";
import { HiCheck, HiThumbDown, HiThumbUp, HiX } from "react-icons/hi";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { translate } from "ui/i18n";
import { BetaPill } from "ui/pages/app/components/activities/BetaPill";
import { useWindowWidth } from "ui/utils/hooks";
import { ModalAIFeedback } from "ui/pages/app/components/activities/AI/feedbacks/ModalAIFeedback";

export function AIFeedbackToast() {
  const isDesktop = useWindowWidth();
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectIsAIFeedbackToastOpen);
  const step = useAppSelector(selectAIToastStep);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const closeAndReset = () => {
    dispatch(setAIToastStep("first"));
    dispatch(closeAIFeedbackToast());
  };

  const handleEvaluationClick = (isPositive: boolean) => {
    if (isPositive) {
      dispatch(setAIToastStep("positive"));
      dispatch(setTechniqueFeedbackValue({ evaluation: "OK" }));
    } else {
      dispatch(setAIToastStep("negative"));
      dispatch(setTechniqueFeedbackValue({ evaluation: "KO" }));
    }
  };

  const handleReasonClick = (reason: string) => {
    if (reason === "other") {
      dispatch(closeAIFeedbackToast());
      setIsFeedbackModalOpen(true);
    } else {
      dispatch(sendAIFeedback(reason));
      dispatch(setAIToastStep("thanks"));
      setTimeout(() => {
        closeAndReset();
      }, 6000);
    }
  };

  const handleSubmitFeedback = async (reason: string) => {
    const result = await dispatch(sendAIFeedback(reason)).unwrap();
    if (result.success) {
      setIsFeedbackModalOpen(false);
      dispatch(setAIToastStep("thanks"));
      dispatch(openAIFeedbackToast());
      setTimeout(() => {
        closeAndReset();
      }, 6000);
    }
  };

  if (isOpen) {
    return (
      <div
        className={`fixed space-y-4 bg-darkGrey-800 px-4 py-5 text-white ${
          isDesktop
            ? "bottom-[40px] right-[40px] max-w-[375px] rounded-[20px]"
            : "bottom-0 left-0 right-0 rounded-t-[20px]"
        }`}
      >
        {step === "first" && (
          <>
            <BetaPill />
            <p className="text-sm font-bold md:text-base">
              {translate("ns1:AI.Toast.FeedbackOnResults")}
            </p>
            <div className="flex items-center space-x-3">
              <button
                className="flex size-12 items-center justify-center rounded-xl bg-white md:size-14"
                onClick={() => handleEvaluationClick(true)}
              >
                <HiThumbUp className="text-darkGrey-800" size={24} />
              </button>
              <button
                className="flex size-12 items-center justify-center rounded-xl bg-white md:size-14"
                onClick={() => handleEvaluationClick(false)}
              >
                <HiThumbDown className="text-darkGrey-800" size={24} />
              </button>
            </div>
          </>
        )}
        {step === "positive" && (
          <>
            <p className="text-sm font-bold md:text-base">
              {translate("ns1:AI.Toast.WhatLiked")}
            </p>
            <div className="flex flex-wrap gap-3">
              <ChoiceButton
                name={translate("ns1:AI.Toast.Relevance")}
                handleReasonClick={() => handleReasonClick("Pertinence")}
              />
              <ChoiceButton
                name={translate("ns1:AI.Toast.Quantity")}
                handleReasonClick={() => handleReasonClick("Quantité")}
              />
              <ChoiceButton
                name={translate("ns1:AI.Toast.Other")}
                handleReasonClick={() => handleReasonClick("other")}
              />
            </div>
          </>
        )}
        {step === "negative" && (
          <>
            <p className="text-sm font-bold md:text-base">
              {translate("ns1:AI.Toast.WhatNotLiked")}
            </p>
            <div className="flex flex-wrap gap-3">
              <ChoiceButton
                name={translate("ns1:AI.Toast.InappropriateResults")}
                handleReasonClick={() =>
                  handleReasonClick("Résultats inadaptés")
                }
              />
              <ChoiceButton
                name={translate("ns1:AI.Toast.NotEnough")}
                handleReasonClick={() =>
                  handleReasonClick("Pas assez de résultats")
                }
              />
              <ChoiceButton
                name={translate("ns1:AI.Toast.Other")}
                handleReasonClick={() => handleReasonClick("other")}
              />
            </div>
          </>
        )}
        {step === "thanks" && (
          <div className="flex h-14 items-center justify-between space-x-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-darkGrey-100">
              <HiCheck size={24} className="text-darkGrey-800" />
            </div>
            <p className="text-base font-bold text-white">
              {translate("ns1:AI.Toast.ThankForFeedback")}
            </p>
            <button className="items-top flex h-full" onClick={closeAndReset}>
              <HiX className="text-white" size={24} />
            </button>
          </div>
        )}
      </div>
    );
  } else if (isFeedbackModalOpen) {
    return (
      <ModalAIFeedback
        isOpen
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={handleSubmitFeedback}
      />
    );
  } else {
    return null;
  }
}

const ChoiceButton = ({
  name,
  handleReasonClick,
}: {
  name: string;
  handleReasonClick: () => void;
}) => {
  const isDesktop = useWindowWidth();

  return (
    <button
      className={`rounded-xl bg-white px-4 text-sm font-bold text-darkGrey-800 text-darkGrey-800 ${
        isDesktop ? "h-[46px]" : "h-[36px]"
      }`}
      onClick={handleReasonClick}
    >
      {name}
    </button>
  );
};
